import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { UpdatePublicScheduleSettingsAPI } from '../../../../api/API';
import styles from './PublicScheduleSettings.module.css';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import { Switch, Alert, Snackbar, Tooltip, Paper } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';

const DOMAIN = (process.env.REACT_APP_DOMAIN_URL || 'https://www.gabriela.app.br').replace(/\/$/, '');

/**
 * PublicScheduleSettings
 *
 * Quando usado standalone (sem props), mantém seu próprio botão de salvar.
 * Quando usado dentro de Preferences, recebe:
 *   - isEditable: boolean — controla se os campos estão habilitados
 *   - ref: expõe o método save() via useImperativeHandle
 */
const PublicScheduleSettings = forwardRef(function PublicScheduleSettings({ isEditable: isEditableProp }, ref) {
    const navigate = useNavigate();
    const dataProvider = DataProviderInstance();
    const { CompanyConfig } = dataProvider;
    const Resources = CompanyConfig?.Resources;
    const Procedures = CompanyConfig?.Procedures;
    const { hasRole } = useRoleAccess();
    const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    // Se isEditableProp for undefined, o componente gerencia seu próprio estado de edição
    const isControlled = isEditableProp !== undefined;
    const isEditable = isControlled ? isEditableProp : canEdit;

    // Company-level settings
    const [enabled, setEnabled] = useState(false);
    const [slug, setSlug] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Derived lists (read-only, apenas para exibição)
    const enabledResources = (Resources || []).filter(r => r.public_schedule_enabled);
    const enabledProcedures = (Procedures || []).filter(p => p.public_schedule_enabled);

    useEffect(() => {
        if (CompanyConfig) {
            setEnabled(!!CompanyConfig.public_schedule_enabled);
            setSlug(CompanyConfig.public_schedule_slug || '');
            setWhatsapp(CompanyConfig.public_schedule_whatsapp || '');
        }
    }, [CompanyConfig]);

    const publicUrl = `${DOMAIN}/agendar/${slug}`;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(publicUrl).then(() => {
            setSnackbar({ open: true, message: 'Link copiado!', severity: 'success' });
        });
    };

    const save = (onDone) => {
        setSaving(true);
        UpdatePublicScheduleSettingsAPI({
            public_schedule_enabled: enabled ? 1 : 0,
            public_schedule_slug: slug,
            public_schedule_whatsapp: whatsapp,
        }, (res) => {
            setSaving(false);
            if (res && res.success) {
                if (res.public_schedule_slug) {
                    setSlug(res.public_schedule_slug);
                }
                setSnackbar({ open: true, message: 'Configurações salvas!', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: res?.error || 'Erro ao salvar.', severity: 'error' });
            }
            if (onDone) onDone(res);
        });
    };

    // Expõe save() para o componente pai via ref
    useImperativeHandle(ref, () => ({ save }));

    return (
        <Paper className={styles.paperWrapper} elevation={0}>
            <AreaTitle
                Icon={<CalendarMonthOutlinedIcon />}
                Title="Agendamento Público"
                Description="Configure a página pública de agendamento online para seus clientes."
            />

            {/* ── Configurações gerais ─────────────────────────────────── */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Configurações gerais</h3>

                <div className={styles.row}>
                    <div className={styles.rowLabel}>
                        <span>Habilitar agendamento público</span>
                        <small>Permite que clientes agendem pelo link público</small>
                    </div>
                    <Switch
                        checked={enabled}
                        onChange={e => setEnabled(e.target.checked)}
                        disabled={!isEditable}
                        color="primary"
                    />
                </div>

                <div className={styles.fieldGroup}>
                    <label className={styles.label}>URL da agenda pública</label>
                    <small className={styles.hint} style={{ display: 'block', marginBottom: 6 }}>
                        Gerada automaticamente a partir do nome da empresa. Você pode personalizar o slug se desejar.
                    </small>
                    {slug ? (
                        <div className={styles.linkRow}>
                            <span className={styles.linkText}>{publicUrl}</span>
                            <Tooltip title="Copiar link">
                                <button className={styles.iconBtn} onClick={handleCopyLink}>
                                    <ContentCopyIcon fontSize="small" />
                                </button>
                            </Tooltip>
                            <Tooltip title="Abrir link">
                                <a href={publicUrl} target="_blank" rel="noreferrer" className={styles.iconBtn}>
                                    <OpenInNewIcon fontSize="small" />
                                </a>
                            </Tooltip>
                        </div>
                    ) : (
                        <small className={styles.hint} style={{ color: '#f59e0b' }}>
                            A URL será gerada automaticamente ao salvar as configurações.
                        </small>
                    )}
                </div>

                <div className={styles.fieldGroup}>
                    <label className={styles.label}>WhatsApp para confirmação</label>
                    <input
                        className={styles.input}
                        value={whatsapp}
                        onChange={e => setWhatsapp(e.target.value)}
                        placeholder="5511999999999"
                        disabled={!isEditable}
                    />
                    <small className={styles.hint}>Número com código do país (ex: 5511999999999). O cliente será redirecionado para confirmar via WhatsApp.</small>
                </div>
            </div>

            {/* ── Profissionais ────────────────────────────────────────── */}
            <div className={styles.section}>
                <div className={styles.sectionHeaderRow}>
                    <h3 className={styles.sectionTitle}>Agendas habilitadas</h3>
                    <button className={styles.btnShortcut} onClick={() => navigate('/company/agendas')}>
                        Gerenciar agendas <ArrowForwardIcon fontSize="small" />
                    </button>
                </div>
                <p className={styles.sectionHint}>
                    Para habilitar uma agenda no agendamento público, acesse <strong>Gerenciar agendas</strong> e ative a opção de agendamento público em cada uma.
                </p>
                {enabledResources.length === 0 ? (
                    <p className={styles.empty}>Nenhuma agenda habilitada para agendamento público ainda.</p>
                ) : (
                    <div className={styles.chipList}>
                        {enabledResources.map(r => (
                            <span key={r.id} className={styles.chip}>
                                {r.public_schedule_name || r.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Serviços ─────────────────────────────────────────────── */}
            <div className={styles.section}>
                <div className={styles.sectionHeaderRow}>
                    <h3 className={styles.sectionTitle}>Serviços habilitados</h3>
                    <button className={styles.btnShortcut} onClick={() => navigate('/company/procedures')}>
                        Gerenciar serviços <ArrowForwardIcon fontSize="small" />
                    </button>
                </div>
                <p className={styles.sectionHint}>
                    Para habilitar um serviço no agendamento público, acesse <strong>Gerenciar serviços</strong> e ative a opção de agendamento público em cada um.
                </p>
                {enabledProcedures.length === 0 ? (
                    <p className={styles.empty}>Nenhum serviço habilitado para agendamento público ainda.</p>
                ) : (
                    <div className={styles.chipList}>
                        {enabledProcedures.map(p => (
                            <span key={p.id} className={styles.chip}>
                                {p.public_schedule_name || p.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
});

export default PublicScheduleSettings;
